import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  PushSubscribedResponse,
  PushSubscribeRequest,
  PushUnsubscribeRequest,
} from '@lasoxp/contracts';
import { useProperty } from '../contexts/PropertyContext';
import { apiService } from '../services/api';
import { API_CONFIG } from '../config/api';
import { devLog, devError } from '../utils/dev-log';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

/** Convert a URL-safe base64 string to a Uint8Array (for applicationServerKey). */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export interface PushNotificationState {
  /** Browser supports Push API + service workers */
  isSupported: boolean;
  /** Current Notification permission state */
  permission: NotificationPermission;
  /** User has an active subscription for the current property */
  isSubscribed: boolean;
  /** Operation in progress */
  isLoading: boolean;
  /** Subscribe to push notifications (triggers browser permission prompt) */
  subscribe: () => Promise<void>;
  /** Unsubscribe from push notifications */
  unsubscribe: () => Promise<void>;
}

export function usePushNotifications(): PushNotificationState {
  const { selectedProperty } = useProperty();
  const propertyId = selectedProperty?.id;

  const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && !!VAPID_PUBLIC_KEY;

  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default',
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  // Register service worker and check existing subscription
  useEffect(() => {
    if (!isSupported || !propertyId) return;

    let cancelled = false;

    (async () => {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        registrationRef.current = reg;

        const sub = await reg.pushManager.getSubscription();
        if (cancelled) return;

        if (sub) {
          // Verify with backend that subscription is still tracked
          try {
            const res = await apiService.get<PushSubscribedResponse>(
              API_CONFIG.endpoints.push.status(propertyId),
            );
            setIsSubscribed(res.subscribed);
          } catch {
            setIsSubscribed(false);
          }
        } else {
          setIsSubscribed(false);
        }

        setPermission(Notification.permission);
      } catch (err) {
        devError('[PushNotifications] Service worker registration failed', err);
      }
    })();

    return () => { cancelled = true; };
  }, [isSupported, propertyId]);

  const subscribe = useCallback(async () => {
    if (!isSupported || !propertyId || !registrationRef.current) return;
    setIsLoading(true);

    try {
      const reg = registrationRef.current;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY).buffer as ArrayBuffer,
      });

      setPermission(Notification.permission);

      const subscribeBody: PushSubscribeRequest = {
        subscription: subscription.toJSON() as unknown as PushSubscribeRequest['subscription'],
      };
      await apiService.post(
        API_CONFIG.endpoints.push.subscribe(propertyId),
        subscribeBody,
      );

      setIsSubscribed(true);
      devLog('[PushNotifications] Subscribed successfully');
    } catch (err) {
      devError('[PushNotifications] Subscribe failed', err);
      setPermission(Notification.permission);
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, propertyId]);

  const unsubscribe = useCallback(async () => {
    if (!propertyId) return;
    setIsLoading(true);

    try {
      const reg = registrationRef.current ?? await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();

      if (sub) {
        // Use POST for unsubscribe since del() doesn't support a body
        const unsubscribeBody: PushUnsubscribeRequest = { endpoint: sub.endpoint };
        await apiService.post(
          API_CONFIG.endpoints.push.unsubscribe(propertyId),
          unsubscribeBody,
        );
        await sub.unsubscribe();
      }

      setIsSubscribed(false);
      devLog('[PushNotifications] Unsubscribed successfully');
    } catch (err) {
      devError('[PushNotifications] Unsubscribe failed', err);
    } finally {
      setIsLoading(false);
    }
  }, [propertyId]);

  return { isSupported, permission, isSubscribed, isLoading, subscribe, unsubscribe };
}
